/**
 * The 3D findings drawer: a side panel over the report with the 3D body, the
 * leg alignment view and the plantar plate — the same tabs, overlays and
 * findings list as the design sample — and the KQ3D icon that pulls it out.
 *
 *   - The KQ3D icon sits beside the KinetiQ Footcare letterhead and in the
 *     report's top bar. Drag it to the left and the drawer slides out from
 *     the right edge under the pointer; let go past about a quarter of its
 *     width (or flick) and it glides open, otherwise it slides back. A tap or
 *     Enter opens it the same way.
 *   - Closes from its close button, Esc, or by dragging its header back to
 *     the right; the report stays where it was.
 *   - Its width is dragged from its left edge, and remembered on this device.
 *   - Screen only: neither the icon nor the drawer prints.
 *   - three.js and the body model load the first time the drawer is pulled.
 *   - Not offered on a browser without WebGL 2, or when the scan has nothing
 *     to show in 3D.
 */

import React, { Suspense, lazy, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { canRender3D } from '../../lib/body3d/support';

const DrawerContent = lazy(() => import('./FindingsDrawerContent'));

const WIDTH_KEY = 'kinetiq.findings3d.width';
const MIN_W = 360;
const readWidth = () => {
  try { const w = Number(localStorage.getItem(WIDTH_KEY)); return w >= MIN_W ? w : 600; } catch { return 600; }
};
const maxWidth = () => Math.max(MIN_W, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 48);
/** How far (share of the drawer) or how fast (px/ms) a pull must go to count. */
const SNAP_SHARE = 0.28, FLICK = 0.45;

export function hasFindings3d(data) {
  return Boolean(data && (data.posture.measured || data.legs.measured || data.feet.measured || data.feet.items.length || data.measure?.measured));
}

/** Whether the drawer can be offered here at all. */
export function canOfferFindings3d(data) {
  return hasFindings3d(data) && canRender3D();
}

/**
 * A horizontal drag on an element. `onMove(dx)` gets the distance from the
 * press (+ is to the right), `onEnd(dx, vx)` the distance and the release
 * speed; a press that never moved is a tap, and calls `onTap`. The element's
 * own click (from the keyboard) also calls `onTap`, once.
 */
function useHorizontalDrag({ onMove, onEnd, onTap, enabled = true }) {
  const g = useRef(null);
  const pointerAt = useRef(-Infinity);
  return useMemo(() => ({
    onPointerDown(e) {
      if (!enabled || (e.button !== undefined && e.button !== 0)) return;
      if (e.target.closest?.('[data-no-drag]')) return;
      g.current = { x: e.clientX, t: performance.now(), lastX: e.clientX, lastT: performance.now(), moved: false, id: e.pointerId };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove(e) {
      const s = g.current;
      if (!s) return;
      const dx = e.clientX - s.x;
      if (!s.moved && Math.abs(dx) < 5) return;
      s.moved = true;
      s.vx = (e.clientX - s.lastX) / Math.max(1, performance.now() - s.lastT);
      s.lastX = e.clientX; s.lastT = performance.now();
      onMove(dx);
    },
    onPointerUp(e) {
      const s = g.current;
      if (!s) return;
      g.current = null;
      e.currentTarget.releasePointerCapture?.(s.id);
      pointerAt.current = performance.now();
      if (s.moved) onEnd(e.clientX - s.x, s.vx ?? 0);
      else onTap?.();
    },
    onPointerCancel() {
      const s = g.current;
      g.current = null;
      if (s?.moved) onEnd(0, 0);
    },
    onClick() {
      // A pointer press has just decided (tap or drag); a keyboard press arrives as a click alone.
      if (performance.now() - pointerAt.current < 500) return;
      onTap?.();
    },
  }), [enabled, onMove, onEnd, onTap]);
}

/**
 * The drawer's state, shared by the drawer and the KQ3D icons that pull it.
 * `pull` is the drawer's offset from its open position, in px, while a
 * finger is on it — null when it is resting open or closed.
 */
export function useFindingsDrawer() {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(readWidth);
  const [pull, setPull] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [iconShift, setIconShift] = useState(0);
  const w = Math.min(width, maxWidth());

  const show = useCallback(() => { setMounted(true); setOpen(true); }, []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => { setMounted(true); setOpen(o => !o); }, []);

  // Dragging an icon to the left pulls the drawer out; the icon follows a little.
  const icon = useHorizontalDrag({
    onMove: useCallback(dx => {
      setMounted(true);
      const left = Math.max(0, -dx);
      setPull(Math.max(0, w - left));
      setIconShift(-Math.min(left, 90) * 0.6);
    }, [w]),
    onEnd: useCallback((dx, vx) => {
      setIconShift(0);
      setPull(null);
      setOpen(-dx > w * SNAP_SHARE || vx < -FLICK);
    }, [w]),
    onTap: toggle,
  });

  // Dragging the open drawer's header to the right pushes it away.
  const head = useHorizontalDrag({
    enabled: open,
    onMove: useCallback(dx => setPull(Math.min(w, Math.max(0, dx))), [w]),
    onEnd: useCallback((dx, vx) => {
      setPull(null);
      if (dx > w * SNAP_SHARE || vx > FLICK) setOpen(false);
    }, [w]),
  });

  return { open, show, close, toggle, width: w, setWidth, pull, mounted, iconShift, icon, head };
}

/** The KQ3D mark: a small cube and the letters, on the brand navy. */
export function KQ3DMark({ size = 34 }) {
  const id = `k3dg${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  return (
    <svg className="k3d-mark" width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#123b50" />
          <stop offset="1" stopColor="#0b2434" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="46" height="46" rx="12" fill={`url(#${id})`} />
      <g className="k3d-cube" fill="none" stroke="#41d8d3" strokeWidth="1.6" strokeLinejoin="round" opacity=".55">
        <path d="M33 9 l7 4 v8 l-7 4 -7 -4 v-8 z" />
        <path d="M26 13 l7 4 7 -4 M33 17 v8" />
      </g>
      <text x="7" y="31" fontFamily="Arial, Helvetica, sans-serif" fontWeight="800" fontSize="15" fill="#ffffff" letterSpacing="-0.5">KQ</text>
      <rect x="7" y="34" width="21" height="9" rx="4.5" fill="#19c7c3" />
      <text x="17.5" y="41.2" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="800" fontSize="7.5" fill="#0b2434">3D</text>
    </svg>
  );
}

/**
 * The KQ3D icon that opens the drawer: tap it, or drag it to the left.
 *   variant "letterhead" — beside the KinetiQ Footcare letterhead, with a hint
 *   variant "bar"        — compact, in the report's top bar
 */
export function KQ3DHandle({ drawer, variant = 'bar' }) {
  const { open, icon, iconShift } = drawer;
  return (
    <button
      type="button"
      className={`k3d-icon k3d-icon-${variant} no-print${open ? ' is-open' : ''}${iconShift ? ' is-pulling' : ''}`}
      style={{ '--shift': `${iconShift}px` }}
      aria-expanded={open}
      aria-controls="k3d-drawer"
      aria-label={open ? 'Close the 3D findings' : 'Open the 3D findings — tap, or drag to the left'}
      title="3D findings — drag to the left or tap"
      data-testid={variant === 'bar' ? 'open-3d-findings' : 'kq3d-letterhead'}
      {...icon}
    >
      <span className="k3d-chevrons" aria-hidden="true"><i /><i /><i /></span>
      <KQ3DMark size={variant === 'letterhead' ? 48 : 30} />
      {variant === 'letterhead' && (
        <span className="k3d-icon-text">
          <b>3D findings</b>
          <small>{open ? 'Open on the right' : 'Drag left or tap'}</small>
        </span>
      )}
    </button>
  );
}

/** The drawer itself. Mounted once first pulled, kept mounted (hidden) after closing so reopening is instant. */
export function FindingsDrawer({ drawer, data, title }) {
  const { open, close, width, setWidth, pull, mounted, head } = drawer;
  const closeRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus({ preventScroll: true });
    const onKey = e => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const onPointerDown = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { x: e.clientX, w: width };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [width]);
  const onPointerMove = useCallback(e => {
    if (!drag.current) return;
    setWidth(Math.min(maxWidth(), Math.max(MIN_W, drag.current.w + (drag.current.x - e.clientX))));
  }, [setWidth]);
  const onPointerUp = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    try { localStorage.setItem(WIDTH_KEY, String(Math.round(width))); } catch { /* per-device nicety only */ }
  }, [width]);
  const onHandleKey = e => {
    // Keyboard resize: arrows widen and narrow the drawer.
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setWidth(w => Math.min(maxWidth(), Math.max(MIN_W, w + (e.key === 'ArrowLeft' ? 40 : -40))));
  };

  if (!mounted) return null;
  const dragging = pull !== null;
  const visible = open || dragging;
  return (
    <aside
      id="k3d-drawer"
      className={`k3d-drawer no-print${open ? ' is-open' : ''}${dragging ? ' is-dragging' : ''}`}
      style={{ '--k3d-w': `${width}px`, ...(dragging ? { transform: `translateX(${pull}px)` } : null) }}
      aria-label="3D findings"
      aria-hidden={!visible}
      inert={!visible}
      data-testid="k3d-drawer"
    >
      <div
        className="k3d-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Drag to resize the 3D findings panel"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onHandleKey}
      >
        <span />
      </div>
      <header className="k3d-head" {...head} onClick={undefined} title="Drag to the right to close">
        <div className="k3d-head-title">
          <KQ3DMark size={30} />
          <div>
            <p className="k3d-eyebrow">3D findings</p>
            <h2>{title}</h2>
          </div>
        </div>
        <button type="button" data-no-drag ref={closeRef} className="k3d-close" onClick={close} aria-label="Close the 3D findings and return to the report">
          <X size={16} aria-hidden="true" />
        </button>
      </header>
      <Suspense fallback={<p className="k3d-loading">Loading the 3D view…</p>}>
        <DrawerContent data={data} active={open} />
      </Suspense>
    </aside>
  );
}

export const K3D_CSS = `
/* The KQ3D icon: drag it to the left, or tap it. */
.k3d-icon { position:relative; display:inline-flex; align-items:center; gap:8px; border:0; background:none; padding:0; cursor:grab; touch-action:pan-y; user-select:none; -webkit-user-select:none; color:#0b2434; transform:translateX(var(--shift, 0)); transition:transform .45s cubic-bezier(.22,1,.36,1); }
.k3d-icon.is-pulling { transition:none; cursor:grabbing; }
.k3d-icon:focus-visible { outline:2px solid #19c7c3; outline-offset:3px; border-radius:12px; }
.k3d-icon .k3d-mark { display:block; flex:none; border-radius:12px; box-shadow:0 4px 14px rgba(11,36,52,.28); animation:k3d-glow 3.2s ease-in-out infinite; }
.k3d-icon:hover .k3d-mark { transform:translateX(-2px); }
.k3d-icon .k3d-cube { transform-origin:33px 17px; animation:k3d-spin 6s ease-in-out infinite; }
.k3d-icon-letterhead { margin-left:auto; padding:6px 8px 6px 4px; border-radius:16px; }
.k3d-icon-letterhead:hover { background:rgba(25,199,195,.08); }
.k3d-icon-text { display:flex; flex-direction:column; text-align:left; line-height:1.2; }
.k3d-icon-text b { font:800 12px Arial,Helvetica,sans-serif; letter-spacing:.02em; }
.k3d-icon-text small { font:600 10px Arial,Helvetica,sans-serif; color:#087f7f; }
/* Three chevrons drifting left: "pull me this way". */
.k3d-chevrons { display:inline-flex; gap:1px; margin-right:-2px; }
.k3d-chevrons i { width:7px; height:7px; border-left:2px solid #19c7c3; border-bottom:2px solid #19c7c3; transform:rotate(45deg); opacity:0; animation:k3d-drift 1.8s ease-in-out infinite; }
.k3d-chevrons i:nth-child(2) { animation-delay:.18s; }
.k3d-chevrons i:nth-child(3) { animation-delay:.36s; }
.k3d-icon.is-open .k3d-chevrons i { animation:none; opacity:.35; transform:rotate(-135deg); }
.k3d-icon-bar .k3d-chevrons i { width:6px; height:6px; }
@keyframes k3d-drift { 0% { opacity:0; translate:6px 0; } 40% { opacity:1; } 100% { opacity:0; translate:-6px 0; } }
@keyframes k3d-glow { 0%,100% { box-shadow:0 4px 14px rgba(11,36,52,.28); } 50% { box-shadow:0 4px 18px rgba(25,199,195,.55); } }
@keyframes k3d-spin { 0%,70%,100% { transform:rotate(0deg); } 85% { transform:rotate(-12deg); } }
.k3d-drawer button:focus-visible, .k3d-handle:focus-visible { outline:2px solid #19c7c3; outline-offset:2px; }
.k3d-drawer { position:fixed; top:0; right:0; bottom:0; z-index:60; width:min(var(--k3d-w), 100vw); background:#f4f7f8; color:#0b2434; border-left:1px solid #d9e4e7; box-shadow:-18px 0 40px rgba(11,36,52,.16); display:flex; flex-direction:column; transform:translateX(calc(100% + 30px)); transition:transform .52s cubic-bezier(.22,1,.36,1), box-shadow .3s; font-family:Arial,Helvetica,sans-serif; }
.k3d-drawer.is-open { transform:none; }
.k3d-drawer.is-dragging { transition:none; box-shadow:-24px 0 50px rgba(11,36,52,.22); }
/* On opening, the stage and the list rise in after the panel. */
.k3d-drawer.is-open .k3d-stage { animation:k3d-rise .55s .12s both cubic-bezier(.22,1,.36,1); }
.k3d-drawer.is-open .k3d-rail { animation:k3d-rise .55s .22s both cubic-bezier(.22,1,.36,1); }
@keyframes k3d-rise { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
@media (prefers-reduced-motion: reduce) {
  .k3d-drawer, .k3d-icon { transition:none; }
  .k3d-icon .k3d-mark, .k3d-icon .k3d-cube, .k3d-chevrons i, .k3d-drawer.is-open .k3d-stage, .k3d-drawer.is-open .k3d-rail { animation:none; }
  .k3d-chevrons i { opacity:.8; }
}
.k3d-handle { position:absolute; left:-7px; top:0; bottom:0; width:14px; cursor:ew-resize; display:flex; align-items:center; justify-content:center; touch-action:none; z-index:2; }
.k3d-handle span { width:4px; height:44px; border-radius:4px; background:#9fb3bb; }
.k3d-handle:hover span { background:#087f7f; }
.k3d-head { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 18px 12px; border-bottom:1px solid #d9e4e7; background:#fff; cursor:grab; touch-action:pan-y; user-select:none; -webkit-user-select:none; }
.k3d-drawer.is-dragging .k3d-head { cursor:grabbing; }
.k3d-head-title { display:flex; align-items:center; gap:10px; }
.k3d-head-title .k3d-mark { border-radius:9px; }
.k3d-head h2 { margin:2px 0 0; font-size:17px; line-height:1.2; }
.k3d-eyebrow { margin:0; font-size:10px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; color:#087f7f; }
.k3d-close { width:34px; height:34px; border-radius:9px; border:1px solid #d9e4e7; background:#fff; color:#0b2434; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; flex:none; }
.k3d-loading { margin:24px 18px; color:#61737c; font-size:13px; }
.k3d-body { flex:1; min-height:0; display:flex; flex-direction:column; }
.k3d-stage { position:relative; flex:none; height:min(54vh, 540px); background:radial-gradient(ellipse at 50% 38%, #f3f6f7 0%, #dfe7eb 72%); overflow:hidden; border-bottom:1px solid #d9e4e7; }
.k3d-gl, .k3d-gl canvas { position:absolute; inset:0; width:100%; height:100%; display:block; }
.k3d-gl canvas { touch-action:none; cursor:grab; }
.k3d-toolbar { flex:none; display:flex; flex-wrap:wrap; gap:6px 8px; justify-content:space-between; align-items:center; padding:8px 10px; background:#fff; border-bottom:1px solid #d9e4e7; }
.k3d-seg, .k3d-toggles { display:flex; gap:3px; background:#f4f7f8; border:1px solid #d9e4e7; border-radius:10px; padding:3px; }
.k3d-toggles { overflow-x:auto; scrollbar-width:none; max-width:100%; }
.k3d-toggles::-webkit-scrollbar { display:none; }
.k3d-seg button, .k3d-toggles button { font:700 12px Arial,Helvetica,sans-serif; color:#61737c; background:none; border:0; border-radius:7px; padding:6px 10px; cursor:pointer; white-space:nowrap; }
.k3d-seg button[aria-pressed="true"] { background:#0b2434; color:#fff; }
.k3d-toggles button[aria-pressed="true"] { color:#087f7f; background:#e6f6f5; }
.k3d-pins { position:absolute; inset:0; pointer-events:none; z-index:2; }
.k3d-pin { position:absolute; transform:translate(-50%, calc(-100% - 7px)); pointer-events:auto; white-space:nowrap; font:700 11px Arial,Helvetica,sans-serif; color:#fff; background:var(--c); border:0; border-radius:999px; padding:4px 9px; box-shadow:0 2px 6px rgba(11,36,52,.22); cursor:pointer; transition:opacity .2s; }
.k3d-pin b { font-weight:400; opacity:.92; }
.k3d-pin::after { content:""; position:absolute; left:50%; bottom:-4px; transform:translateX(-50%); border:4px solid transparent; border-bottom:0; border-top-color:var(--c); }
.k3d-pin.dim { opacity:.35; }
.k3d-hint { position:absolute; z-index:3; left:12px; right:220px; bottom:9px; font:11px ui-monospace,Menlo,monospace; color:#61737c; pointer-events:none; line-height:1.35; }
.k3d-legend { position:absolute; z-index:3; right:12px; bottom:8px; display:flex; gap:9px; font:11px ui-monospace,Menlo,monospace; color:#61737c; pointer-events:none; }
.k3d-hint .short { display:none; }
.k3d-reset { position:absolute; z-index:3; top:10px; right:10px; font:700 11px Arial,Helvetica,sans-serif; color:#087f7f; background:rgba(255,255,255,.92); border:1px solid #d9e4e7; border-radius:999px; padding:6px 11px; cursor:pointer; }
.k3d-legend span::before { content:""; display:inline-block; width:8px; height:8px; border-radius:50%; margin-right:4px; background:var(--c); }
.k3d-msg { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; text-align:center; padding:0 40px; margin:0; font-size:13px; color:#61737c; z-index:1; }
.k3d-rail { flex:1; min-height:0; overflow:auto; padding:14px 16px 24px; }
.k3d-rail h3 { margin:0 0 10px; font-size:11px; letter-spacing:.1em; text-transform:uppercase; color:#526774; }
.k3d-finding { width:100%; text-align:left; display:grid; grid-template-columns:5px 1fr auto; gap:12px; align-items:center; background:#fff; border:1px solid #d9e4e7; border-radius:10px; padding:11px 13px; margin:0 0 8px; cursor:pointer; color:inherit; font:inherit; }
.k3d-finding[aria-pressed="true"] { border-color:#0b2434; box-shadow:0 0 0 1px #0b2434; }
.k3d-finding .stripe { align-self:stretch; border-radius:3px; background:var(--c); }
.k3d-finding .name { font-weight:700; font-size:14px; }
.k3d-finding .meta { font-size:12px; color:#61737c; }
.k3d-finding .val { text-align:right; font:600 15px ui-monospace,Menlo,monospace; white-space:nowrap; }
.k3d-finding .val small { display:block; font-size:10px; font-weight:400; color:#61737c; }
.k3d-mtitle-main { font:800 20px Arial,Helvetica,sans-serif !important; letter-spacing:-.01em !important; text-transform:none !important; color:#13b6a6 !important; margin:2px 0 12px !important; }
.k3d-measure { margin-bottom:10px; }
.k3d-mfeet { display:grid; grid-template-columns:1fr 1fr; border-top:2px solid #0b2434; border-bottom:1px solid #d9e4e7; padding:9px 0; text-align:center; font:800 11px Arial,Helvetica,sans-serif; letter-spacing:.06em; text-transform:uppercase; color:#13b6a6; }
.k3d-mrow { border-bottom:1px solid #d9e4e7; }
.k3d-mrow > header { position:relative; display:flex; align-items:center; justify-content:center; padding:9px 40px; border-bottom:1px solid #e6ecef; }
.k3d-mrow h4 { margin:0; font:800 11px Arial,Helvetica,sans-serif; letter-spacing:.06em; text-transform:uppercase; color:#0b2434; }
.k3d-mplus { position:absolute; right:2px; width:28px; height:28px; border-radius:50%; border:1.5px solid #0b2434; background:#fff; color:#0b2434; font:400 19px/1 Arial,Helvetica,sans-serif; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; }
.k3d-mplus[aria-pressed="true"] { background:#0b2434; color:#fff; }
.k3d-mcells { display:grid; grid-template-columns:1fr 1fr; }
.k3d-mcells > :first-child { border-right:1px solid #d9e4e7; }
.k3d-mcell { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:2px; padding:12px 6px; min-height:64px; text-align:center; }
.k3d-mval { font:800 28px Arial,Helvetica,sans-serif; letter-spacing:-.02em; color:#0b2434; }
.k3d-mval small, .k3d-msub small { font-size:15px; font-weight:600; letter-spacing:0; }
.k3d-mtitle { font:800 22px Arial,Helvetica,sans-serif; color:#0b2434; letter-spacing:-.01em; }
.k3d-msub { font:700 14px Arial,Helvetica,sans-serif; color:#0b2434; }
.k3d-micon svg { width:72px; height:72px; }
.k3d-micon b { font:800 14px Arial,Helvetica,sans-serif; color:#0b2434; }
.k3d-micon small { font-size:11px; color:#61737c; }
.k3d-mempty { color:#9fb3bb; font-size:20px; }
.k3d-mhead { font:800 18px Arial,Helvetica,sans-serif !important; letter-spacing:-.01em !important; text-transform:none !important; color:#13b6a6 !important; text-align:center; margin:18px 0 8px !important; padding-top:12px; border-top:2px solid #0b2434; }
.k3d-note { font-size:12px; color:#61737c; margin:6px 0 0; line-height:1.5; }
.k3d-chain { background:#fff; border:1px solid #d9e4e7; border-radius:10px; padding:11px 13px; font-size:12.5px; line-height:1.55; margin-top:10px; }
.k3d-chain b { display:block; font:700 10px ui-monospace,Menlo,monospace; letter-spacing:.08em; color:#087f7f; margin-bottom:4px; }
@media (max-width: 640px) {
  .k3d-drawer { width:100vw; }
  .k3d-handle { display:none; }
  .k3d-stage { height:52vh; }
  .k3d-toolbar { padding:8px; }
  .k3d-seg { width:100%; }
  .k3d-seg { overflow-x:auto; scrollbar-width:none; }
  .k3d-seg::-webkit-scrollbar { display:none; }
  .k3d-seg button { flex:1 0 auto; padding:7px 8px; }
  .k3d-mval { font-size:24px; }
  .k3d-mtitle { font-size:18px; }
  .k3d-toggles { width:100%; }
  .k3d-toggles button { flex:1 0 auto; }
  .k3d-hint { right:12px; bottom:30px; font-size:10px; }
  .k3d-hint .long { display:none; }
  .k3d-hint .short { display:inline; }
  .k3d-legend { gap:7px; font-size:10px; }
}
@media print { .k3d-drawer, .k3d-icon { display:none !important; } }
`;
